/**
 * Presentation maps for execution readiness.
 *
 * The backend's readiness states are BLOCKED / AT_RISK / READY / IN_PROGRESS /
 * VERIFIED_COMPLETE, with UNKNOWN ranked worse than BLOCKED ("we don't know"
 * is not "go"). The UI presents them in construction language — READY becomes
 * "Can start now", AT_RISK becomes "Conditional" — without inventing any
 * state the backend didn't assert.
 */
import {
  PlayCircle,
  AlertTriangle,
  OctagonAlert,
  Hammer,
  CheckCircle2,
  HelpCircle,
  Truck,
  Stamp,
  KeyRound,
  HardHat,
  Ship,
  UserCog,
  GitBranch,
  FileText,
  Lock,
  SearchCheck,
  ClipboardList,
} from "lucide-react";

export const READINESS_DISPLAY = {
  READY: {
    label: "CAN START NOW",
    className: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30",
    Icon: PlayCircle,
    order: 0,
  },
  AT_RISK: {
    label: "CONDITIONAL",
    className: "bg-amber-500/20 text-amber-400 border-amber-500/30",
    Icon: AlertTriangle,
    order: 1,
  },
  BLOCKED: {
    label: "BLOCKED",
    className: "bg-red-500/20 text-red-400 border-red-500/30",
    Icon: OctagonAlert,
    order: 2,
  },
  IN_PROGRESS: {
    label: "IN PROGRESS",
    className: "bg-blue-500/20 text-blue-400 border-blue-500/30",
    Icon: Hammer,
    order: 3,
  },
  VERIFIED_COMPLETE: {
    label: "COMPLETE",
    className: "bg-slate-500/20 text-slate-400 border-slate-500/30",
    Icon: CheckCircle2,
    order: 4,
  },
  UNKNOWN: {
    label: "UNKNOWN",
    className: "bg-muted text-muted-foreground border-border",
    Icon: HelpCircle,
    order: 5,
  },
};

export const readinessDisplay = (state) =>
  READINESS_DISPLAY[state] || READINESS_DISPLAY.UNKNOWN;

/**
 * Canonical What Can Start statuses — contract "what-can-start-2".
 * These are backend verdicts; the UI translates them to construction
 * language and never reconstructs them from gate arithmetic.
 */
export const STARTABILITY_DISPLAY = {
  CAN_START_NOW: {
    label: "CAN START NOW",
    className: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30",
    Icon: PlayCircle,
    order: 0,
  },
  CAN_START_CONDITIONALLY: {
    label: "CAN START CONDITIONALLY",
    className: "bg-amber-500/20 text-amber-400 border-amber-500/30",
    Icon: AlertTriangle,
    order: 1,
  },
  BLOCKED: {
    label: "BLOCKED",
    className: "bg-red-500/20 text-red-400 border-red-500/30",
    Icon: OctagonAlert,
    order: 2,
  },
  IN_PROGRESS: {
    label: "IN PROGRESS",
    className: "bg-blue-500/20 text-blue-400 border-blue-500/30",
    Icon: Hammer,
    order: 3,
  },
  COMPLETE: {
    label: "COMPLETE",
    className: "bg-slate-500/20 text-slate-400 border-slate-500/30",
    Icon: CheckCircle2,
    order: 4,
  },
  UNKNOWN: {
    label: "UNKNOWN",
    className: "bg-muted text-muted-foreground border-border",
    Icon: HelpCircle,
    order: 5,
  },
};

export const startabilityDisplay = (status) =>
  STARTABILITY_DISPLAY[status] || STARTABILITY_DISPLAY.UNKNOWN;

/** The named facets of the what-can-start payload, in display order. */
export const FACET_LABELS = {
  scope: "Scope",
  predecessors: "Predecessors",
  material: "Material",
  permit: "Permit",
  inspections: "Inspection",
  access: "Access",
  labor: "Labor",
  submittals: "Submittals",
  loto: "LOTO",
  permitRequirements: "Permit reqs",
  inspectionRequirements: "Inspection reqs",
  accessRequirements: "Access reqs",
  ers: "ERS",
};

/** Realism classifications — contract "project-realism-1". */
export const REALISM_DISPLAY = {
  REALISTIC: {
    label: "REALISTIC",
    className: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30",
    Icon: CheckCircle2,
  },
  CONDITIONALLY_REALISTIC: {
    label: "CONDITIONALLY REALISTIC",
    className: "bg-amber-500/20 text-amber-400 border-amber-500/30",
    Icon: AlertTriangle,
  },
  AT_RISK: {
    label: "AT RISK",
    className: "bg-orange-500/20 text-orange-400 border-orange-500/30",
    Icon: AlertTriangle,
  },
  INFEASIBLE: {
    label: "INFEASIBLE",
    className: "bg-red-500/20 text-red-400 border-red-500/30",
    Icon: OctagonAlert,
  },
  UNKNOWN: {
    label: "UNKNOWN",
    className: "bg-muted text-muted-foreground border-border",
    Icon: HelpCircle,
  },
};

export const realismDisplay = (classification) =>
  REALISM_DISPLAY[classification] || REALISM_DISPLAY.UNKNOWN;

/** The 12 readiness gates, in backend order, with construction labels. */
export const GATE_LABELS = {
  scope_defined: { label: "Scope gate", Icon: ClipboardList },
  submittal_submitted: { label: "Submittal submitted", Icon: FileText },
  submittal_approved: { label: "Submittal approved", Icon: FileText },
  material_ordered: { label: "Material ordered", Icon: Truck },
  fabrication_complete: { label: "Fabrication", Icon: Truck },
  delivered_to_site: { label: "Delivered to site", Icon: Truck },
  material_sworn_in: { label: "Material verified", Icon: Truck },
  predecessor_complete: { label: "Predecessor", Icon: GitBranch },
  access_available: { label: "Access", Icon: KeyRound },
  permit_approved: { label: "Permit", Icon: Stamp },
  inspection_cleared: { label: "Inspection", Icon: SearchCheck },
  labor_available: { label: "Labor", Icon: HardHat },
};

export const gateLabel = (key) =>
  GATE_LABELS[key]?.label || String(key || "gate").replaceAll("_", " ");

export const GATE_STATUS_STYLES = {
  PASSED: "bg-emerald-500/15 text-emerald-400 border-emerald-500/25",
  FAILED: "bg-red-500/15 text-red-400 border-red-500/25",
  NOT_REQUIRED: "bg-muted/40 text-muted-foreground border-border",
  UNKNOWN: "bg-amber-500/10 text-amber-400/80 border-amber-500/20",
};

/** Constraint sources → icons (drivingConstraint / governingConstraint / gates). */
export const CONSTRAINT_ICONS = {
  material: Truck,
  permit: Stamp,
  access: KeyRound,
  labor: HardHat,
  logistics: Ship,
  operator: UserCog,
  predecessor: GitBranch,
  submittal: FileText,
  loto: Lock,
  inspection: SearchCheck,
};

export const constraintIcon = (source) =>
  CONSTRAINT_ICONS[String(source || "").toLowerCase()] || AlertTriangle;

/**
 * One plain-language sentence for why an activity is not free to start,
 * built strictly from what the backend said.
 */
export const plainBlockingSentence = (activity) => {
  const c = activity?.governingConstraint;
  if (typeof c?.detail === "string" && c.detail.length > 0) return c.detail;
  if (typeof c?.name === "string" && c.name.length > 0) {
    const source = typeof c.source === "string" && c.source.length > 0 ? c.source : "a";
    return `Governed by ${source} constraint: ${c.name}.`;
  }

  // `instances` and `blockingGates` come off the what-can-start payload, and
  // `|| []` is not a shape guard. It let two different lies through here.
  //
  // A non-array `instances` threw a TypeError on .find during render and took
  // the Gantt page down with it. Worse, a `blockingGates` that arrived as the
  // STRING "abc" survived `|| []`, flattened to a single element, and rendered
  //
  //     "Blocked at: abc."
  //
  // — an unreadable payload presented to a superintendent as a NAMED GATE, in
  // the one sentence whose whole job is to say why work cannot start. A
  // fabricated gate is worse than no sentence, so when the payload cannot be
  // read this returns null and the caller says nothing.
  const instances = Array.isArray(activity?.instances) ? activity.instances : [];
  const inst = instances.find(
    (i) => i && typeof i.blockingReason === "string" && i.blockingReason.length > 0,
  );
  if (inst) return inst.blockingReason;

  const gates = instances.flatMap((i) =>
    i && Array.isArray(i.blockingGates) ? i.blockingGates : [],
  );
  const named = gates.filter((g) => typeof g === "string" && g.length > 0);
  if (named.length) return `Blocked at: ${named.map((g) => gateLabel(g)).join(", ")}.`;
  return null;
};
